import type { FC } from 'react'
import { useState } from 'react'
import styled from '@emotion/styled'

import Image from '@segment/matcha/components/Image'

import HeroBase, * as HeroRecipe from '@segment/matcha/recipes/Hero'
import CopyTextBase, * as CopyText from '@segment/matcha/recipes/CopyText'

import EmailCaptureForm from '@segment/templates/Forms/EmailCaptureForm'

import HeroImagePng2x from './assets/HeroImage@2x.png'
import HeroImageWebp from './assets/HeroImage.webp'
import HeroImageWebp2x from './assets/HeroImage@2x.webp'

const copy = {
  overline: `Spring 2023 Report`,
  heading: `The Leading Customer Data Platform`,
  content: `Based on 500+ reviews, Segment is a top ranked CDP with 96% of users rating 4 or 5 stars, 91% customer recommendation rating, 90% ease of use score, and the largest market presence amongst all vendors`,
}

const form = {
  formId: `g2-report-spring23`,
  additionalTrackEvent: `Content Downloaded`,
  overrideConsent: true,
  buttonText: `Get the report`,
  successButtonText: `View the report`,
  buttonUrl: `https://www.g2.com/reports/grid-report-for-customer-data-platform-cdp-spring-2023.embed?featured=segment&secure%5Bgated_consumer%5D=1d201e9d-4109-4b1d-b725-90f75200e092&secure%5Btoken%5D=6a42b7c038b0226567bdeb04b0584bd2d734edb44be9b0967806a5e6f7f69c77&utm_campaign=gate-1886689`,
}

const Hero: FC = () => {
  const [isSubmitted, setIsSubmitted] = useState(false)

  return (
    <Wrapper>
      <HeroBase variant="2Col">
        <HeroRecipe.Copy>
          <CopyTextBase>
            <CopyText.Overline tag="h5" variant="h5">
              {copy.overline}
            </CopyText.Overline>
            <CopyText.Heading tag="h1" variant="h1">
              {copy.heading}
            </CopyText.Heading>
            <CopyText.Description tag="p" variant="pExtraLarge">
              {copy.content}
            </CopyText.Description>
          </CopyTextBase>
          <FormWrapper id="form-wrapper" variant={isSubmitted ? `wide` : `narrow`}>
            <EmailCaptureForm
              resourceType="report"
              customFormIdentifier={form.formId}
              additionalTrackEvent={form.additionalTrackEvent}
              overrideConsent={form.overrideConsent}
              buttonText={form.buttonText}
              successButtonText={form.successButtonText}
              buttonUrl={form.buttonUrl}
              onSubmit={() => setIsSubmitted(true)}
              enableJobTitleField={true}
            />
          </FormWrapper>
        </HeroRecipe.Copy>
        <HeroRecipe.Contents>
          <HeroImage />
        </HeroRecipe.Contents>
      </HeroBase>
    </Wrapper>
  )
}

const Wrapper = styled.header`
  background: ${({ theme }) => theme.palette.twilio.green[10]};
`

const HeroImage: FC = () => (
  <picture>
    <source type="image/webp" srcSet={`${HeroImageWebp}, ${HeroImageWebp2x} 2x`} />
    <Image src={HeroImagePng2x} alt={`Illustration: ${copy.heading}}`} width="513" height="515" loading="lazy" />
  </picture>
)

interface FormWrapperProps {
  variant: `wide` | `narrow`
}
const FormWrapper = styled.div<FormWrapperProps>`
  max-width: ${({ variant }) => (variant === `narrow` ? `417px` : `unset`)};
  margin-top: 38px;
  margin-bottom: 32px;

  ${({ theme }) => theme.breakpoints.up(`lg`)} {
    margin-top: 48px;
    margin-bottom: 48px;
  }
`

export default Hero
